import React, { useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import {
  TextViewer,
  ImageViewer,
  VideoViewer,
  MSViewer,
  PDFViewer,
} from "./index";

const getFileType = (name) => {
  const extension = name?.toLowerCase().split(".").pop();
  return extension || "unknown";
};

const getViewerType = (fileExt, type) => {
  if (type === "link") return "link";

  switch (fileExt) {
    case "pdf":
      return "application/pdf";
    case "doc":
    case "docx":
      return "application/msword";
    case "xls":
    case "xlsx":
      return "application/vnd.ms-excel";
    case "ppt":
    case "pptx":
      return "application/vnd.ms-powerpoint";
    case "txt":
      return "text/plain";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "image";
    default:
      return "unknown";
  }
};

const textDecoder = (encodedText) => {
  return Buffer.from(encodedText, "base64").toString("utf-8");
};

const imageDecoder = (encodedImage, fileExt) => {
  return `data:image/${fileExt};base64,${encodedImage}`;
};

const pdfDecoder = (encodedPDF) => {
  return `data:application/pdf;base64,${encodedPDF}`;
};

const UniversalDocViewer = ({
  documentUrl,
  filename,
  downloadAttachmentContent,
  type = "file",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documentContent, setDocumentContent] = useState(null);

  const fileExt = getFileType(filename);
  const viewerType = getViewerType(fileExt, type);

  const handleLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleError = () => {
    setIsLoading(false);
    setError("Failed to load document.");
  };

  useEffect(() => {
    const fetchDocumentContent = async () => {
      setIsLoading(true);
      try {
        const response = await downloadAttachmentContent();
        setIsLoading(false);
        return response;
      } catch (err) {
        setError("Failed to load document.");
        setIsLoading(false);
        return null;
      }
    };

    if (viewerType === "text/plain") {
      fetchDocumentContent().then((content) =>
        setDocumentContent(textDecoder(content))
      );
    }

    if (viewerType === "image") {
      fetchDocumentContent().then((content) =>
        setDocumentContent(imageDecoder(content, fileExt))
      );
    }

    if (viewerType === "application/pdf") {
      fetchDocumentContent().then((base64) =>
        setDocumentContent(pdfDecoder(base64))
      );
    }
  }, [documentUrl, viewerType]);

  const renderViewer = () => {
    switch (viewerType) {
      case "text/plain":
        return <TextViewer textContent={documentContent} />;
      case "image":
        return (
          <ImageViewer imageContent={documentContent} filename={filename} />
        );
      case "link":
        return (
          <VideoViewer
            url={documentUrl}
            handleLoad={handleLoad}
            handleError={handleError}
          />
        );

      case "application/pdf":
        return <PDFViewer data={documentContent} title={filename} />;

      default:
        return (
          <MSViewer
            url={documentUrl}
            docType={viewerType}
            handleLoad={handleLoad}
            handleError={handleError}
          />
        );
    }
  };

  return (
    <div className="w-100 d-flex align-items-center justify-content-center">
      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-4">
          <FeatherIcon icon="alert-circle" size={16} className="mr-2" />
          <span>{error}</span>
        </div>
      )}

      {isLoading && (
        <div className="d-flex align-items-center justify-content-center">
          Loading document preview...
        </div>
      )}

      <div
        className={`position-relative w-100 h-100 ${
          isLoading ? "d-none" : "d-flex justify-content-center"
        }`}
      >
        {renderViewer()}
      </div>
    </div>
  );
};

export default UniversalDocViewer;
